import {Point2D} from '../math/point.js';

/**
 * List of interaction event names.
 */
export const InteractionEventNames = [
  'mousedown',
  'mousemove',
  'mouseup',
  'mouseout',
  'wheel',
  'dblclick',
  'touchstart',
  'touchmove',
  'touchend',
  'touchcancel'
];

/**
 * List of the interaction event names whose default can be prevented
 * by tools (see `LayerGroupPointer`): their listeners are not passive.
 */
export const NonPassiveInteractionEventNames = [
  'wheel',
  'touchstart'
];

/**
 * Get the listener options of an interaction event: only the
 * {@link NonPassiveInteractionEventNames} are not passive.
 *
 * @param {string} eventName The interaction event name.
 * @returns {AddEventListenerOptions} The listener options.
 */
export function getInteractionEventOptions(eventName) {
  return {
    passive: !NonPassiveInteractionEventNames.includes(eventName)
  };
}

/**
 * Get the positions (without the parent offset) of a list of touch events.
 *
 * @param {TouchList} touches The list of touch events.
 * @param {DOMRect} rect The bounding client rect.
 * @returns {Point2D[]} The list of positions of the touch events.
 */
function getTouchesPositions(touches, rect) {
  // compensate for margin, padding...
  let offsetLeft = 0;
  let offsetTop = 0;
  if (typeof rect !== 'undefined') {
    offsetLeft = rect.left;
    offsetTop = rect.top;
  }

  // set its position
  const positions = [];
  for (let i = 0; i < touches.length; ++i) {
    positions.push(new Point2D(
      touches[i].clientX - offsetLeft,
      touches[i].clientY - offsetTop
    ));
  }
  return positions;
}

/**
 * Get the offsets of an input touch event.
 *
 * @param {TouchEvent} event The event to get the offset from.
 * @returns {Point2D[]} The array of points.
 */
export function getTouchPoints(event) {
  const rect = /** @type {HTMLElement} */ (event.currentTarget)
    .getBoundingClientRect();

  let positions = [];
  if (typeof event.targetTouches !== 'undefined' &&
    event.targetTouches.length !== 0) {
    // see https://developer.mozilla.org/en-US/docs/Web/API/TouchEvent/targetTouches
    positions = getTouchesPositions(event.targetTouches, rect);
  } else if (typeof event.changedTouches !== 'undefined' &&
    event.changedTouches.length !== 0) {
    // see https://developer.mozilla.org/en-US/docs/Web/API/TouchEvent/changedTouches
    positions = getTouchesPositions(event.changedTouches, rect);
  }
  return positions;
}

/**
 * Get the offset of an input mouse event.
 *
 * @param {MouseEvent} event The event to get the offset from.
 * @returns {Point2D} The 2D point.
 */
export function getMousePoint(event) {
  // offsetX/Y: the offset in the X coordinate of the mouse pointer
  // between that event and the padding edge of the target node
  // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/offsetX
  // https://caniuse.com/mdn-api_mouseevent_offsetx
  return new Point2D(
    event.offsetX,
    event.offsetY
  );
}

/**
 * Test if a canvas with the input size can be created.
 *
 * Ref:
 * - {@link https://github.com/ivmartel/dwv/issues/902},
 * - {@link https://github.com/jhildenbiddle/canvas-size/blob/v1.2.4/src/canvas-test.js}.
 *
 * @param {number} width The canvas width.
 * @param {number} height The canvas height.
 * @returns {boolean} True is the canvas can be created.
 */
export function canCreateCanvas(width, height) {
  // test canvas with input size
  const testCvs = document.createElement('canvas');
  testCvs.width = width;
  testCvs.height = height;
  // crop canvas to speed up test
  const cropCvs = document.createElement('canvas');
  cropCvs.width = 1;
  cropCvs.height = 1;
  // contexts
  const testCtx = testCvs.getContext('2d');
  const cropCtx = cropCvs.getContext('2d');
  // set data
  if (testCtx) {
    testCtx.fillRect(width - 1, height - 1, 1, 1);
    // Render the test pixel in the bottom-right corner of the
    // test canvas in the top-left of the 1x1 crop canvas. This
    // dramatically reducing the time for getImageData to complete.
    cropCtx.drawImage(testCvs, width - 1, height - 1, 1, 1, 0, 0, 1, 1);
  }
  // Verify image data (alpha component, Pass = 255, Fail = 0)
  return cropCtx && cropCtx.getImageData(0, 0, 1, 1).data[3] !== 0;
}
